import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import {
  AssistedProduct,
  SummaryResponse,
  TopProduct,
} from '@/webapi/use-analytics-api';
import { HSpace, VSpace } from '@/components/spacing';
import { SummaryContext } from '@/features/details/summary/context';
import {
  formattedRevenue,
  ProductTile,
  selectStyles,
  ShowMoreButton,
  Title,
} from '@/features/details/charts/assisted-products/index';
import { currencySymbol } from '@/utils/currencies';
import { CONTROL } from '@/features/details/shared';
import { isBestSellersAvailable } from '@/webapi/models';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { breakpoints, isMobile } from '@/components/responsive';

export const TopProducts: FC<{
  summaryData: SummaryResponse;
  isAudience: boolean;
}> = ({ summaryData, isAudience }) => {
  const { metricsStartAt, variants } = useContext(SummaryContext);

  const {
    controlId,
    nonControlId,
    filterLeft,
    setFilterLeft,
    filterRight,
    setFilterRight,
    def1,
    def2,
    options,
    hasOnlyOneVariant,
  } = useTopProducts(variants, isAudience);
  const symbol = currencySymbol();

  const { optsLeft, optsRight, leftProducts, rightProducts } = useOptions(
    options,
    filterRight,
    filterLeft,
    summaryData,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const isMobileDevice = isMobile();

  const isEnabled = isBestSellersAvailable(metricsStartAt);
  if (!isEnabled || (summaryData?.bestSellers?.length || 0) === 0) {
    return null;
  }

  if (hasOnlyOneVariant) {
    return (
      <>
        <Container key={`k1${controlId}${nonControlId}`}>
          <Column wide>
            <VSpace value={2.4} className="only-desktop" />
            <Select
              className="title full"
              menuPlacement="bottom"
              styles={selectStyles}
              isDisabled={options.length === 1}
              defaultValue={def1}
              onChange={(x: any) => {
                setFilterLeft(x.value);
              }}
              options={options}
            />
            <VSpace value={5} className="only-desktop" />
            <VSpace value={3} className="only-mobile" />
            <Grid
              className={`${isExpanded ? `expanded` : `collapsed`}`}
              twoRows
            >
              {summaryData?.bestSellers
                ?.filter((p) => p.variantId === filterLeft)
                ?.slice(0, 9)
                ?.map((p) => (
                  <ProductTile key={p.productId}>
                    <Revenue>
                      {symbol}
                      {formattedRevenue(p as unknown as AssistedProduct)}
                      {` | ${p.items} items`}
                    </Revenue>
                    <ImageBox>
                      <img key={p.imageSrc} src={p.imageSrc} alt="produt" />
                    </ImageBox>
                    <Title>{p?.title || ``}</Title>
                  </ProductTile>
                ))}
            </Grid>
          </Column>
        </Container>
        {!isExpanded && isMobileDevice && (
          <ShowMoreButton onClick={() => setIsExpanded(true)}>
            Show More
          </ShowMoreButton>
        )}
      </>
    );
  }

  return (
    <>
      <Container key={`k${controlId}${nonControlId}`}>
        <Column>
          <VSpace
            className="only-desktop"
            value={optsLeft.length === 1 ? 2 : 4}
          />
          <Select
            className="title"
            menuPlacement="bottom"
            styles={selectStyles}
            isDisabled={optsLeft.length === 1}
            defaultValue={def1}
            onChange={(x: any) => {
              setFilterLeft(x.value);
            }}
            options={optsLeft}
          />
          <VSpace className="only-desktop" value={5} />
          <VSpace value={3} className="only-mobile" />
          <Grid className={`${isExpanded ? `expanded` : `collapsed`}`}>
            {leftProducts?.map((p) => (
              <ProductTile
                key={p.productId}
                style={{ opacity: p.hidden ? 0 : 1 }}
              >
                <Revenue>
                  {symbol}
                  {formattedRevenue(p as unknown as AssistedProduct)}
                  {` | ${p.items} items`}
                </Revenue>
                <ImageBox>
                  <img key={p.imageSrc} src={p.imageSrc} alt="produt" />
                </ImageBox>
                <Title>{p?.title || ``}</Title>
              </ProductTile>
            ))}
          </Grid>
        </Column>
        <HSpace value={2} className="only-desktop" />
        <HSpace value={1} className="only-mobile" />
        <Column>
          <VSpace
            className="only-desktop"
            value={optsRight.length === 1 ? 2 : 4}
          />
          <Select
            className="title"
            menuPlacement="bottom"
            styles={selectStyles}
            isDisabled={optsRight.length === 1}
            defaultValue={def2}
            onChange={(x: any) => {
              setFilterRight(x.value);
            }}
            options={optsRight}
          />
          <VSpace value={5} className="only-desktop" />
          <VSpace value={3} className="only-mobile" />
          <Grid className={`${isExpanded ? `expanded` : `collapsed`}`}>
            {rightProducts?.map((p) => (
              <ProductTile
                key={p.productId}
                style={{ opacity: p.hidden ? 0 : 1 }}
              >
                <Revenue>
                  {symbol}
                  {formattedRevenue(p as unknown as AssistedProduct)}
                  {` | ${p.items} items`}
                </Revenue>
                <ImageBox>
                  <img key={p.imageSrc} src={p.imageSrc} alt="produt" />
                </ImageBox>
                <Title>{p?.title || ``}</Title>
              </ProductTile>
            ))}
          </Grid>
        </Column>
      </Container>
      {!isExpanded && isMobileDevice && (
        <ShowMoreButton onClick={() => setIsExpanded(true)}>
          Show More
        </ShowMoreButton>
      )}
    </>
  );
};

const Column = styled.div<{ wide?: boolean }>`
  min-height: 330px;
  display: flex;
  flex-direction: column;
  width: ${(p) => (p.wide ? 100 : 50)}%;

  background: white;
  border: solid 1px #eaeef0;
  box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.15),
    0 4px 11px 0 rgba(151, 210, 206, 0.22);
  padding: 0 2rem 3.5rem 2rem;
  border-radius: 2rem;

  ${breakpoints.down(`md`)} {
    padding: 1rem;
    border-radius: 0.8rem;
    min-height: 240px;

    .title * {
      font-size: 1.4rem;
    }

    .title {
      *[class*='ValueContainer'] {
        max-width: 158px;
        text-wrap: auto;
        line-height: 1.5;
      }
    }

    .title.full {
      *[class*='ValueContainer'] {
        max-width: 300px;
        text-wrap: auto;
        line-height: 1.5;
      }
    }
  }
`;
const Revenue = styled.span`
  background-color: #5d8ef2;
  position: absolute;
  top: -14px;
  left: 10px;
  color: white;
  max-width: 144px;
  border-radius: 20px;
  padding: 0.5rem;
  box-shadow: rgb(0 0 0 /14%) 0 3px 0.8rem 0;
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Container = styled.div`
  width: 100%;
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;

  ${breakpoints.down(`md`)} {
    width: 90%;
    margin: 2rem auto 0 auto;
  }
`;

export const ImageBox = styled.div`
  width: 120px;
  height: 120px;
  overflow: hidden;

  img {
    aspect-ratio: 16/16;
    width: 100%;

    object-fit: cover;
    object-position: left;

    border-radius: 15px;
  }
`;

const Grid = styled.div<{ twoRows?: boolean }>`
  font-family: 'JetBrains Mono', serif;
  user-select: none;
  cursor: default;
  display: grid;
  grid-template-columns: ${(p) =>
    p.twoRows ? `1fr 1fr 1fr 1fr 1fr 1fr` : `1fr 1fr 1fr`};
  grid-gap: 3rem;

  ${breakpoints.down(`md`)} {
    grid-template-columns: ${(p) => (p.twoRows ? `1fr 1fr` : `1fr`)};

    &&.collapsed {
      && > *:nth-child(n + ${(p) => (p.twoRows ? `5` : `3`)}) {
        display: none;
      }
    }
  }
`;

function useTopProducts(
  variants: Array<ExperienceVariant>,
  isAudience: boolean,
) {
  const publishedVariants = variants?.filter((v) => v.publishedChance > 0);
  let controlId = publishedVariants.find((v) => v.name === CONTROL)?.id;
  const variantIds: Array<string> = publishedVariants
    .filter((v) => v.name !== CONTROL)
    ?.map((v) => v.id);
  if (!controlId) {
    controlId = variantIds?.[0];
  }

  const nonControlId = variantIds?.find((v) => v !== controlId);
  const [filterLeft, setFilterLeft] = useState(controlId);
  const [filterRight, setFilterRight] = useState(nonControlId);
  useEffect(() => {
    setFilterLeft(controlId);
    setFilterRight(nonControlId);
  }, [controlId, nonControlId]);

  const def1 = {
    value: variantIds?.find((v) => v === controlId),
    label: isAudience
      ? `Best Selling Items`
      : `Best Selling Items - ${
          publishedVariants?.find((v) => v.id === controlId)?.name || ``
        }`,
  };
  const def2 = {
    value: variantIds?.find((v) => v !== controlId),
    label: isAudience
      ? `Best Selling Items`
      : `Best Selling Items - ${
          publishedVariants?.find((v) => v.id === nonControlId)?.name || ``
        }`,
  };

  const options = [
    ...(publishedVariants?.map((v) => ({
      value: v.id,
      label: `Best Selling Items - ${v.name}`,
    })) || []),
  ];

  const hasOnlyOneVariant = publishedVariants?.length === 1;
  return {
    controlId,
    nonControlId,
    filterLeft,
    setFilterLeft,
    filterRight,
    setFilterRight,
    def1,
    def2,
    options,
    hasOnlyOneVariant,
  };
}

function useOptions(
  options: {
    label: string;
    value: string;
  }[],
  filterRight: string,
  filterLeft: string,
  summaryData: SummaryResponse,
) {
  const optsLeft = options?.filter((o) => o.value !== filterRight) || [];
  const optsRight = options?.filter((o) => o.value !== filterLeft) || [];
  let leftProducts =
    summaryData?.bestSellers
      ?.filter((p) => p.variantId === filterLeft)
      ?.slice(0, 9) || [];
  let rightProducts =
    summaryData?.bestSellers
      ?.filter((p) => p.variantId === filterRight)
      ?.slice(0, 9) || [];
  const size = Math.max(leftProducts.length, rightProducts.length);
  if (leftProducts.length < size) {
    leftProducts = [
      ...leftProducts,
      ...Array.from(
        { length: size - leftProducts.length },
        () =>
          ({
            hidden: true,
          } as TopProduct),
      ),
    ];
  }
  if (rightProducts.length < size) {
    rightProducts = [
      ...rightProducts,
      ...Array.from(
        { length: size - rightProducts.length },
        () =>
          ({
            hidden: true,
          } as TopProduct),
      ),
    ];
  }
  return { optsLeft, optsRight, leftProducts, rightProducts };
}
